import { useContext } from "react"
import Budget2023Context from "../context/budget2023/Budget2023Context"

import {
  MARGINS,
  REDUCTIONS,
  TOTALS,
  MEASUREMENTS,
  UNITS,
  DEFAULT_DECIMALS,
} from "constants/decimals"

export function useDecimalFormatter() {
  const contextValue = useContext(Budget2023Context) || {}
  const coefficients =
    contextValue.coefficients || contextValue.state?.coefficients
  const decimalsConfig =
    coefficients && Object.keys(coefficients).length > 0
      ? coefficients
      : DEFAULT_DECIMALS

  function getDecimals(type) {
    return typeof decimalsConfig[type] === "number" ? decimalsConfig[type] : 2
  }

  function roundToDecimals(value, type) {
    const decimals = getDecimals(type)
    if (value === null || value === undefined || isNaN(Number(value))) {
      return 0
    }
    const factor = Math.pow(10, decimals)
    return Math.round(Number(value) * factor) / factor
  }

  function formatValue(value, type) {
    const decimals = getDecimals(type)
    if (value === null || value === undefined || isNaN(Number(value))) {
      return (0).toFixed(decimals)
    }
    return Number(value).toFixed(decimals)
  }

  function getTotalFromUnit(price, quantity) {
    const numPrice = roundToDecimals(price, UNITS)
    const numQty = roundToDecimals(quantity, MEASUREMENTS)
    if (isNaN(numPrice) || isNaN(numQty)) {
      return 0
    }
    return roundToDecimals(numPrice * numQty, TOTALS)
  }

  function getUnitFromTotal(total, quantity) {
    const numTotal = roundToDecimals(total, TOTALS)
    const numQty = roundToDecimals(quantity, MEASUREMENTS)
    if (isNaN(numTotal) || isNaN(numQty) || numQty === 0) {
      return 0
    }
    return roundToDecimals(numTotal / numQty, UNITS)
  }

  function getUnitWithMargin(price, margin) {
    const numPrice = roundToDecimals(price, UNITS)
    const numMargin = roundToDecimals(margin, MARGINS)
    if (isNaN(numPrice) || isNaN(numMargin)) {
      return 0
    }
    const numMarginDecimal = numMargin / 100
    return roundToDecimals(numPrice * (1 + numMarginDecimal), UNITS)
  }

  function getReductionFromUnit(price, margin) {
    const numPrice = roundToDecimals(price, UNITS)
    const numReduction = roundToDecimals(margin, REDUCTIONS)
    if (isNaN(numPrice) || isNaN(numReduction)) {
      return 0
    }
    const numReductionDecimal = numReduction / 100
    return roundToDecimals(numPrice * (1 - numReductionDecimal), UNITS)
  }

  // Por migrar? Deprecar?
  // calcApplyDiscount -> getClientPriceWithDiscount
  function getClientPriceWithDiscount(clientPrice, discount) {
    const price = roundToDecimals(clientPrice, CLIENT_PRICE)
    const disc = roundToDecimals(discount, MARGINS)
    if (isNaN(price) || isNaN(disc)) {
      return 0
    }
    return roundToDecimals(price * (1 - disc / 100), CLIENT_PRICE)
  }

  // applyCostMargin -> getPriceWithCostMargin
  function getPriceWithCostMargin(price, marginPercent) {
    const numPrice = roundToDecimals(price, UNITS)
    const numMargin = roundToDecimals(marginPercent, MARGINS)
    if (isNaN(numPrice) || isNaN(numMargin)) {
      return 0
    }
    return roundToDecimals(numPrice * (1 + numMargin / 100), UNITS)
  }

  // applyClientMargin -> getPriceWithClientMargin
  function getPriceWithClientMargin(price, marginPercent) {
    const numPrice = roundToDecimals(price, UNITS)
    const numMargin = roundToDecimals(marginPercent, MARGINS)
    if (isNaN(numPrice) || isNaN(numMargin) || numMargin >= 100) {
      return 0
    }
    const divisor = 1 - numMargin / 100
    return roundToDecimals(divisor !== 0 ? numPrice / divisor : 0, UNITS)
  }

  function getPercent(value, total) {
    const numValue = Number(value)
    const numTotal = Number(total)
    if (isNaN(numValue) || isNaN(numTotal) || numTotal === 0) {
      return 0
    }
    const percent = (numValue / numTotal) * 100
    return roundToDecimals(percent, MARGINS)
  }

  return {
    decimals: decimalsConfig,
    formatValue,
    roundToDecimals,
    getTotalFromUnit,
    getUnitFromTotal,
    getUnitWithMargin,
    getReductionFromUnit,

    getClientPriceWithDiscount,
    getPriceWithCostMargin,
    getPriceWithClientMargin,
    getPercent,
    getDecimals,
  }
}
